/**
 * RSI / MACD pane geometry. Same 900-wide x-scale as the price chart; a
 * separate height so a study pane cannot silently inherit CHART_HEIGHT.
 */
import {
  CHART_MAX_POINTS,
  CHART_PRECISION,
  CHART_WIDTH,
  OSCILLATOR_HEIGHT,
  OSCILLATOR_PAD,
} from '../constants/chart';

import { chartScale, chartXAt, sampleEvenlyIndices, type ChartRange } from './chart-path';

function lastDrawable(values: readonly (number | null)[]): number | null {
  const found = [...values].reverse().find(isDrawable);
  return found ?? null;
}

export interface OscillatorGuide {
  readonly key: string;
  readonly y: number;
  readonly x1: number;
  readonly x2: number;
}

export interface OscillatorBar {
  readonly key: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly positive: boolean;
}

export interface OscillatorGeometry {
  readonly viewBox: string;
  readonly line: string | null;
  readonly signal: string | null;
  readonly guides: readonly OscillatorGuide[];
  readonly bars: readonly OscillatorBar[];
  readonly last: number | null;
  readonly signalLast: number | null;
  readonly histogramLast: number | null;
  readonly high: number;
  readonly low: number;
  readonly dataHigh: number;
  readonly dataLow: number;
  readonly pointCount: number;
}

export interface OscillatorOptions {
  readonly domain?: ChartRange;
  readonly guides?: readonly number[];
  readonly histogram?: readonly (number | null)[];
  readonly signal?: readonly (number | null)[];
}

function isDrawable(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value);
}

function oscillatorYAt(value: number, range: ChartRange): number {
  const plotH = OSCILLATOR_HEIGHT - OSCILLATOR_PAD * 2;
  const span = range.hi - range.lo;
  if (span === 0) return OSCILLATOR_PAD + plotH / 2;
  return OSCILLATOR_PAD + (1 - (value - range.lo) / span) * plotH;
}

function sampleIndices(count: number): readonly number[] {
  if (count <= CHART_MAX_POINTS) {
    return Array.from({ length: count }, (_unused, i) => i);
  }
  return sampleEvenlyIndices(count, CHART_MAX_POINTS);
}

function finiteOf(values: readonly (number | null)[]): readonly number[] {
  return values.filter(isDrawable);
}

function alignedCompanion(
  values: readonly (number | null)[] | undefined,
  count: number,
): readonly (number | null)[] {
  if (values?.length !== count) return [];
  return values;
}

function dataRange(
  values: readonly (number | null)[],
  options: OscillatorOptions,
): ChartRange | null {
  const extras = [
    ...alignedCompanion(options.histogram, values.length),
    ...alignedCompanion(options.signal, values.length),
  ];
  return chartScale([...finiteOf(values), ...finiteOf(extras)]);
}

function paneDomain(
  data: ChartRange,
  hist: readonly (number | null)[],
  options: OscillatorOptions,
): ChartRange {
  if (options.domain !== undefined) return options.domain;
  if (hist.length === 0) return data;
  return { lo: Math.min(data.lo, 0), hi: Math.max(data.hi, 0) };
}

/* eslint-disable functional/no-let, functional/no-loop-statements, functional/immutable-data */

function oscillatorLinePath(values: readonly (number | null)[], range: ChartRange): string | null {
  const count = values.length;
  if (count < 2) return null;
  const parts: string[] = [];
  let drawing = false;
  for (const index of sampleIndices(count)) {
    const value = values[index];
    if (!isDrawable(value)) {
      drawing = false;
      continue;
    }
    const x = chartXAt(index, count).toFixed(CHART_PRECISION);
    const y = oscillatorYAt(value, range).toFixed(CHART_PRECISION);
    parts.push(`${drawing ? 'L' : 'M'}${x} ${y}`);
    drawing = true;
  }
  return parts.some((part) => part.startsWith('L')) ? parts.join('') : null;
}

/* eslint-enable functional/no-let, functional/no-loop-statements, functional/immutable-data */

function guideMarks(levels: readonly number[], range: ChartRange): readonly OscillatorGuide[] {
  return levels.map((level) => ({
    key: String(level),
    y: oscillatorYAt(level, range),
    x1: 0,
    x2: CHART_WIDTH,
  }));
}

function histogramBarWidth(count: number, indices: readonly number[]): number {
  if (count <= 1) return CHART_WIDTH;
  const first = indices[0] ?? 0;
  const next = indices[1] ?? 1;
  return Math.max(1, (chartXAt(next, count) - chartXAt(first, count)) * 0.6);
}

function histogramBar(
  index: number,
  value: number,
  range: ChartRange,
  layout: { readonly count: number; readonly width: number },
): OscillatorBar | null {
  const yZero = oscillatorYAt(0, range);
  const yVal = oscillatorYAt(value, range);
  const height = Math.abs(yZero - yVal);
  if (height === 0) return null;
  const x = layout.count <= 1 ? 0 : chartXAt(index, layout.count) - layout.width / 2;
  return {
    key: `h-${String(index)}`,
    x,
    y: Math.min(yZero, yVal),
    width: layout.width,
    height,
    positive: value > 0,
  };
}

function histogramBars(
  values: readonly (number | null)[] | undefined,
  count: number,
  range: ChartRange,
): readonly OscillatorBar[] {
  if (values?.length !== count) return [];
  const indices = sampleIndices(count);
  const layout = { count, width: histogramBarWidth(count, indices) };
  return indices.flatMap((index) => {
    const value = values[index];
    if (!isDrawable(value)) return [];
    const bar = histogramBar(index, value, range, layout);
    return bar === null ? [] : [bar];
  });
}

function hasDrawablePlot(values: readonly (number | null)[], options: OscillatorOptions): boolean {
  if (values.some(isDrawable)) return true;
  if (alignedCompanion(options.histogram, values.length).some(isDrawable)) return true;
  return alignedCompanion(options.signal, values.length).some(isDrawable);
}

export function buildOscillatorGeometry(
  values: readonly (number | null)[],
  options: OscillatorOptions = {},
): OscillatorGeometry | null {
  if (!hasDrawablePlot(values, options)) return null;
  const data = dataRange(values, options);
  if (data === null) return null;
  const histValues = alignedCompanion(options.histogram, values.length);
  const range = paneDomain(data, histValues, options);
  const signalValues = alignedCompanion(options.signal, values.length);
  return {
    viewBox: `0 0 ${String(CHART_WIDTH)} ${String(OSCILLATOR_HEIGHT)}`,
    line: oscillatorLinePath(values, range),
    signal: oscillatorLinePath(signalValues, range),
    guides: guideMarks(options.guides ?? [], range),
    bars: histogramBars(options.histogram, values.length, range),
    last: lastDrawable(values),
    signalLast: lastDrawable(signalValues),
    histogramLast: lastDrawable(histValues),
    high: range.hi,
    low: range.lo,
    dataHigh: data.hi,
    dataLow: data.lo,
    pointCount: values.length,
  };
}
